import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header/Header";
import SectionRow from "../components/SectionRow/SectionRow";
import ContinueWatching from "../components/ContinueWatching/ContinueWatching";
import PlayerModal from "../components/PlayerModal/PlayerModal";
import scene from "../styles/Scene.module.css";
import useLocalStorageMap from "../hooks/useLocalStorageMap";
import { getVideosApi } from "../http/http";

const STORAGE_KEYS = {
  likes: "kids_portal_likes_v1",
  progress: "kids_portal_progress_v1",
};

export default function HomePage() {
  const [query, setQuery] = useState("");
  const [likes, setLikes] = useLocalStorageMap(STORAGE_KEYS.likes, {});
  const [progress, setProgress] = useLocalStorageMap(STORAGE_KEYS.progress, {});
  const [playing, setPlaying] = useState(null);
  const [offsets, setOffsets] = useState({ cloudsX: 0, cloudsY: 0, hillsY: 0 });
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizeApi = (payload) => {
    const arr = Array.isArray(payload) ? payload : [payload];
    const map = {};
    for (const block of arr) {
      const cat = block?.category || "Misc";
      if (!map[cat]) map[cat] = [];
      for (const it of block?.data || []) {
        if (it?.status !== 1) continue;
        map[cat].push({
          id: it.id,
          category: it.category || cat,
          title: it.video_name,
          url: it.video_link,
          image: it.image_link,
          duration: it?.duration,
        });
      }
    }
    return map;
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getVideosApi();
      const map = normalizeApi(res?.data);
      setCategories(map);
    } catch (err) {
      setError(err?.message || "Failed to load videos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setOffsets({ cloudsX: -(y * 0.1), cloudsY: (y * 0.05), hillsY: -(y * 0.08) });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // const allItems = useMemo(() => Object.values(seedVideos).flat(), []);
  const allItems = useMemo(() => Object.values(categories).flat(), [categories]);

  const continueWatching = useMemo(() => allItems.filter(v => {
    const p = progress[v.id]; if (!p) return false; const pct = p.d ? (p.t / p.d) * 100 : 0; return pct > 1 && pct < 98;
  }), [allItems, progress]);

  const filterByQuery = (items) => {
    const q = query.trim().toLowerCase(); if (!q) return items; return items.filter(v => v.title.toLowerCase().includes(q));
  };

  const toggleLike = (id) => setLikes(prev => ({ ...prev, [id]: !prev[id] }));
  const openPlayer = (item) => { setPlaying(item); setProgress(prev => ({ ...prev, [item.id]: prev[item.id] || { t: 0, d: item.duration || 1 } })); };
  const closePlayer = () => setPlaying(null);

  const setProgTime = (id, t, d) => setProgress(prev => ({ ...prev, [id]: { t, d } }));
  const completeProg = (id, d) => setProgress(prev => ({ ...prev, [id]: { t: d, d } }));

  const getPct = (id) => { const p = progress[id]; if (!p || !p.d) return 0; return Math.max(0, Math.min(100, Math.round((p.t / p.d) * 100))); };


  // ---------- Loader (simple spinner) ----------
  const Spinner = () => (
    <div style={{ display: "grid", placeItems: "center", minHeight: "40vh" }}>
      <style>
        {`@keyframes spin{to{transform:rotate(360deg)}}
          .spin{width:44px;height:44px;border:4px solid #e5e7eb;border-top-color:#7c3aed;border-radius:50%;animation:spin 1s linear infinite}`}
      </style>
      <div className="spin" />
      <div style={{ marginTop: 10, color: "#6b7280" }}>Loading videos…</div>
    </div>
  );

  // ---------- Error UI ----------
  const ErrorView = ({ message, onRetry }) => (
    <div style={{ display: "grid", placeItems: "center", minHeight: "40vh" }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #f3f4f6",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 8px 20px rgba(0,0,0,.06)",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Couldn’t load videos</div>
        <div style={{ color: "#6b7280", marginBottom: 12 }}>{message}</div>
        <button
          onClick={onRetry}
          style={{
            border: 0,
            background: "#f7fbff",
            borderRadius: 10,
            padding: "8px 12px",
            borderBottom: "1px solid #e6f2ff",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <div className={scene.scene}>
      {/* Parallax layers */}
      <div className={`${scene.layer} ${scene.sun}`} style={{ transform: `translateY(${offsets.hillsY * 0.4}px)` }} />
      <div className={`${scene.layer} ${scene.clouds}`} style={{ backgroundPosition: `${offsets.cloudsX}px ${40 + offsets.cloudsY}px` }} />
      <div className={`${scene.layer} ${scene.hills}`} style={{ transform: `translateY(${offsets.hillsY}px)` }} />

      <Header query={query} onQueryChange={setQuery} />

      <main className={scene.container}>
        <section className={scene.hero}>
          <div className={scene.card}>
            <div className={`${scene.bubble} ${scene.b1}`} />
            <div className={`${scene.bubble} ${scene.b2}`} />
            <div className={`${scene.bubble} ${scene.b3}`} />
            <h1 className={scene.title}>Welcome to KidsMania 👋</h1>
            <p className={scene.sub}>
              {/* Colorful rhymes, playful learning, and cozy stories — all in one cheerful place. Safe, simple, and fun! */}
              Online educational platform for digital skills where students aged between 2-10 learn through fun and interactive videos
            </p>
            <div className="pill" style={{ marginTop: 10, position: "relative", zIndex: 2 }}>✨ Tip: Tap ❤ to save favorites</div>
          </div>
          {/* <div className={scene.art}>
            <div className={`${scene.bubble} ${scene.b1}`}/>
            <div className={`${scene.bubble} ${scene.b2}`}/>
            <div className={`${scene.bubble} ${scene.b3}`}/>
          </div> */}
        </section>

        {loading && <Spinner />}
        {!loading && error && <ErrorView message={error} onRetry={fetchData} />}

        {!loading && !error && (
          <>

            <section style={{ marginTop: 26 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ fontSize: 20, margin: 0 }}>Continue Watching</h2>
                <div className="pill">⏳ Picks you left off</div>
              </div>
              <div className={scene.scroll}>
                <ContinueWatching items={continueWatching} likes={likes} onPlay={openPlayer} onToggleLike={toggleLike} getPct={getPct} />
              </div>
            </section>

            {Object.entries(categories).map(([cat, items]) => (
              <SectionRow
                key={cat}
                label={cat}
                items={filterByQuery(items)}
                likes={likes}
                onPlay={openPlayer}
                onToggleLike={toggleLike}
                getPct={getPct}
              />
            ))}
          </>
        )
        }
      </main>

      <PlayerModal
        openItem={playing}
        onClose={closePlayer}
        onProgress={setProgTime}
        onComplete={completeProg}
      />
    </div>
  );
}
